// Employment & Engagements (multi-entity workforce Phase 3).
//
// A person may work for several companies of the group at once — an employee
// of one, an independent contractor of another. This card lists ONLY the
// engagements the viewer may see: the server fences them by company (owner
// decision O-2), so an Afromark-only controller does not learn that this person
// also works for Fern — the count included. The person sees all their own.
//
// Pay is not shown here: salary lives in Payroll (per company), contractor fees
// in Contractor & External Pay.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Briefcase, ChevronDown, ChevronUp, Loader2, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { useConfirm, usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission } from '@/rbac/useRbac';
import { getCompensation, setCompensation } from '@/lib/compensationApi';
import { RepaymentMoveCallout } from './RepaymentMoveCallout';
import { AttendanceRulesEditor } from './AttendanceRulesEditor';
import {
  engagementsApi, type Engagement, type EngagementTermsRow, type SameEntityTaxFinding,
} from './engagementsApi';
import {
  RELATIONSHIP_LABEL, STATUS_LABEL, STATUS_VARIANT, actionsFor, countLine, isSecondaryEmployment, pairFindingState,
  sortEngagements,
  termsSummary,
  type EngagementAction,
} from './engagementRules';
import { EngagementWizard } from './EngagementWizard';

interface Props {
  employeeId: string;
  personName: string;
  /** The viewer is this person (they always see their own engagements). */
  isSelf: boolean;
}

const ACTION_LABEL: Record<EngagementAction, string> = {
  submit: 'Submit for approval',
  approve: 'Approve',
  reject: 'Send back',
  suspend: 'Suspend',
  resume: 'Resume',
  end: 'End',
  edit: 'Change position',
};

const today = () => new Date().toISOString().slice(0, 10);

/** Refetch delays after an engagement change (ms): now, +1.5 s, then +3.5 s more (≈5 s in all). */
const PAIR_REFRESH_DELAYS_MS = [0, 1500, 3500];

const findingsFingerprint = (fs: SameEntityTaxFinding[] | null) =>
  fs === null ? 'error' : fs.map((f) => `${f.id}:${f.generation}:${f.status}:${f.provisional}`).sort().join('|');

export const EngagementsCard: React.FC<Props> = ({ employeeId, personName, isSelf }) => {
  const canView = useHasPermission('engagement:view');
  const canManage = useHasPermission('engagement:manage');
  const canApprove = useHasPermission('engagement:approve');
  const canViewPay = useHasPermission('compensation:view');
  const canEditPay = useHasPermission('compensation:edit');
  const { t } = useTranslation('employees');
  const [pay, setPay] = useState<Record<string, string>>({});
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [rows, setRows] = useState<Engagement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [history, setHistory] = useState<Record<string, EngagementTermsRow[] | 'loading'>>({});
  // ME7: payroll's same-company pair findings (null = not loaded / not allowed).
  const [pairFindings, setPairFindings] = useState<SameEntityTaxFinding[] | null>(null);
  const [pairError, setPairError] = useState<string | null>(null);
  const canSeePairTax = canViewPay || canEditPay;
  const pairFindingsRef = useRef<SameEntityTaxFinding[] | null>(null);
  const pairSyncRun = useRef(0);
  // Sequence of findings requests: only the LATEST one may update the block —
  // after two quick changes the earlier refetch can resolve last (web #45
  // follow-up), and its older answer must not replace the newer one.
  const pairLoadSeq = useRef(0);
  const mounted = useRef(true);
  const [pairSync, setPairSync] = useState<'idle' | 'syncing' | 'unchanged' | 'cleared'>('idle');

  const load = useCallback(async () => {
    setError(null);
    try {
      const out = await engagementsApi.list(employeeId);
      setRows(sortEngagements(out.engagements));
    } catch (e) {
      setRows(null);
      setError(getApiErrorMessage(e, 'Could not load engagements.'));
    }
  }, [employeeId]);

  useEffect(() => {
    if (canView || isSelf) void load();
  }, [canView, isSelf, load]);

  // Resolves to the findings, null on error, or undefined when a newer request
  // superseded this one (its answer is dropped; the newer one owns the state).
  const loadPairFindings = useCallback(async (): Promise<SameEntityTaxFinding[] | null | undefined> => {
    const seq = ++pairLoadSeq.current;
    setPairError(null);
    try {
      const out = await engagementsApi.sameEntityTaxFindings(employeeId);
      if (seq !== pairLoadSeq.current) return undefined;
      if (!out || !Array.isArray(out.findings)) throw new Error('malformed findings');
      setPairFindings(out.findings);
      pairFindingsRef.current = out.findings;
      // "No longer reports" describes an EMPTY answer. A later (latest) answer
      // with findings makes it false, whichever reload brought them (web #48
      // follow-up).
      if (out.findings.length > 0) setPairSync((s) => (s === 'cleared' ? 'idle' : s));
      return out.findings;
    } catch (e) {
      if (seq !== pairLoadSeq.current) return undefined;
      // Never an empty "all clear": a failed load is shown as an error.
      setPairFindings(null);
      pairFindingsRef.current = null;
      setPairError(getApiErrorMessage(e, t('engagements.pairTax.loadError')));
      return null;
    }
  }, [employeeId, t]);

  // After an engagement changes (create/submit, approve, reject, suspend,
  // resume, end, terms), payroll re-derives the pair findings from the
  // engagement EVENTS — asynchronously, so the first refetch may still show
  // the old state. Refetch at once, then (while nothing has changed) twice more
  // at +1.5 s and +5 s, and stop at the first change. While catching up the block
  // says it is updating; if nothing changed by then it says so — it never
  // presents an unchanged answer as the outcome of the change.
  const refreshPairFindingsAfterChange = useCallback(async () => {
    if (!canSeePairTax) return;
    const run = ++pairSyncRun.current;
    const before = findingsFingerprint(pairFindingsRef.current);
    const hadFindings = (pairFindingsRef.current?.length ?? 0) > 0;
    // Changed: a successful EMPTY answer after there were findings is said as
    // such ("no longer reports"), never as "resolved"; anything else is idle.
    const settled = (now: SameEntityTaxFinding[] | null) =>
      (hadFindings && now !== null && now.length === 0 ? 'cleared' : 'idle');
    setPairSync('syncing');
    for (const wait of PAIR_REFRESH_DELAYS_MS) {
      if (wait) await new Promise((r) => setTimeout(r, wait));
      if (run !== pairSyncRun.current || !mounted.current) return;   // superseded or unmounted
      const got = await loadPairFindings();
      if (run !== pairSyncRun.current || !mounted.current) return;
      if (got === undefined) continue;                                // a newer request owns the block
      if (got === null || findingsFingerprint(got) !== before) {
        setPairSync(settled(got));                                    // changed (or an error is shown)
        return;
      }
    }
    // A superseded last attempt: judge by what the block now shows.
    setPairSync(findingsFingerprint(pairFindingsRef.current) !== before ? settled(pairFindingsRef.current) : 'unchanged');
  }, [canSeePairTax, loadPairFindings]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if ((canView || isSelf) && canSeePairTax) void loadPairFindings();
  }, [canView, isSelf, canSeePairTax, loadPairFindings]);

  if (!canView && !isSelf) return null;

  const run = async (e: Engagement, action: EngagementAction) => {
    const company = e.legal_entity_name || 'this company';
    let call: (() => Promise<unknown>) | null = null;
    if (action === 'submit') call = () => engagementsApi.submit(e.id);
    if (action === 'approve') {
      const ok = await confirm({
        title: 'Approve engagement',
        message: `${personName} becomes an active ${RELATIONSHIP_LABEL[e.relationship_type].toLowerCase()} of ${company}.`,
        confirmLabel: 'Approve',
      });
      if (!ok) return;
      call = () => engagementsApi.approve(e.id);
    }
    let suspendFrom: string | null = null;
    if (action === 'suspend') {
      suspendFrom = await prompt({
        title: `Suspend engagement with ${company}`,
        message: 'Pay from this company stops for the suspended working days only. The engagement is not ended.',
        label: 'First suspended day',
        inputType: 'date',
        required: true,
        defaultValue: today(),
        confirmLabel: 'Next',
      });
      if (!suspendFrom) return;
    }
    if (action === 'reject' || action === 'suspend') {
      const reason = await prompt({
        title: action === 'reject' ? 'Send back for changes' : `Suspend engagement with ${company}`,
        label: 'Reason',
        multiline: true,
        required: true,
        minLength: 3,
        confirmLabel: action === 'reject' ? 'Send back' : 'Suspend',
        danger: action === 'suspend',
      });
      if (!reason) return;
      call = () =>
        action === 'reject' ? engagementsApi.reject(e.id, reason) : engagementsApi.suspend(e.id, reason, suspendFrom ?? undefined);
    }
    if (action === 'resume') {
      const from = await prompt({
        title: `Resume engagement with ${company}`,
        message: 'Pay from this company restarts on this day.',
        label: 'First day paid again',
        inputType: 'date',
        required: true,
        defaultValue: today(),
        confirmLabel: 'Resume',
      });
      if (!from) return;
      call = () => engagementsApi.resume(e.id, from);
    }
    if (action === 'end') {
      const endDate = await prompt({
        title: `End engagement with ${company}`,
        message: "Only this engagement ends. The person's other engagements are not affected; if it is their last one, they leave the organisation.",
        label: 'Last day',
        inputType: 'date',
        required: true,
        defaultValue: today(),
        confirmLabel: 'Next',
      });
      if (!endDate) return;
      const reason = await prompt({
        title: `End engagement with ${company}`,
        label: 'Reason',
        multiline: true,
        required: true,
        minLength: 3,
        confirmLabel: 'End engagement',
        danger: true,
      });
      if (!reason) return;
      call = () => engagementsApi.end(e.id, endDate, reason);
    }
    if (action === 'edit') {
      const position = await prompt({
        title: `Change position at ${company}`,
        label: 'Position',
        required: true,
        defaultValue: e.terms.position ?? '',
        confirmLabel: 'Next',
      });
      if (!position) return;
      const from = await prompt({
        title: `Change position at ${company}`,
        label: 'Effective from',
        inputType: 'date',
        required: true,
        defaultValue: today(),
        confirmLabel: 'Save',
      });
      if (!from) return;
      call = () => engagementsApi.changeTerms(e.id, { position }, from, 'position change');
    }
    if (!call) return;
    setBusy(e.id);
    try {
      await call();
      toast.success(`${ACTION_LABEL[action]}: done`);
      setHistory((h) => {
        const n = { ...h };
        delete n[e.id];
        return n;
      });
      await load();
      void refreshPairFindingsAfterChange();
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Could not ${ACTION_LABEL[action].toLowerCase()}.`), { duration: 7000 });
    } finally {
      setBusy(null);
    }
  };

  // ── secondary employment pay (payroll subject = the engagement id) ──────────
  const showPay = async (e: Engagement) => {
    try {
      const rows = await getCompensation(e.id);
      const cur = rows.find((r) => r.active !== false && !r.effective_to) ?? rows[0];
      setPay((p) => ({ ...p, [e.id]: cur ? `${cur.currency} ${Number(cur.amount).toLocaleString()} from ${cur.effective_from}` : 'No salary set yet' }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load the salary for this employment.'));
    }
  };

  const setSalary = async (e: Engagement) => {
    const company = e.legal_entity_name || 'this company';
    const amount = await prompt({
      title: `Salary at ${company}`,
      message: 'Monthly base salary for THIS employment only, paid by this company\'s payroll.',
      label: 'Amount (TZS)',
      required: true,
      confirmLabel: 'Next',
    });
    if (!amount) return;
    if (!/^\d+(\.\d{1,2})?$/.test(amount.replace(/,/g, '').trim())) {
      toast.error('Enter the amount as a number, e.g. 1500000.');
      return;
    }
    const from = await prompt({ title: `Salary at ${company}`, label: 'Effective from', inputType: 'date', required: true, defaultValue: today(), confirmLabel: 'Save' });
    if (!from) return;
    setBusy(e.id);
    try {
      await setCompensation(e.id, { amount: amount.replace(/,/g, '').trim(), effective_from: from });
      toast.success(`Salary at ${company} saved`);
      if (canViewPay) await showPay(e);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save the salary.'), { duration: 7000 });
    } finally {
      setBusy(null);
    }
  };

  const confirmTax = async (e: Engagement) => {
    const note = await prompt({
      title: 'Confirm the tax treatment of this second employment',
      message: "This company's normal PAYE is applied — no rate is changed automatically. Confirm that this is right for the person (e.g. after checking with your tax adviser).",
      label: 'What was confirmed',
      multiline: true,
      required: true,
      minLength: 3,
      confirmLabel: 'Confirm',
    });
    if (!note) return;
    setBusy(e.id);
    try {
      await engagementsApi.confirmSecondEmploymentTax(e.id, note);
      toast.success('Tax treatment confirmed');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not record the confirmation.'), { duration: 7000 });
    } finally {
      setBusy(null);
    }
  };

  // ME7: confirm ONE payroll finding — the generation payroll returned, never a
  // client guess. Ended pairs keep a finding for their historical overlap, so
  // this works whatever state the engagements are in now.
  const companyName = (entityId: string) =>
    rows?.find((r) => r.legal_entity_id === entityId)?.legal_entity_name || 'this company';

  const confirmPairTax = async (f: SameEntityTaxFinding) => {
    const company = companyName(f.legal_entity_id);
    const note = await prompt({
      title: t('engagements.pairTax.title', { company }),
      message: t('engagements.pairTax.message', { company, name: personName }),
      label: t('engagements.pairTax.label'),
      multiline: true,
      required: true,
      minLength: 3,
      confirmLabel: t('engagements.pairTax.confirm'),
    });
    if (!note || !note.trim()) return;
    setBusy(f.id);
    try {
      await engagementsApi.confirmSameEntityTax(employeeId, f, note.trim());
      toast.success(t('engagements.pairTax.done', { company }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('engagements.pairTax.saveError')), { duration: 7000 });
    } finally {
      setBusy(null);
      void loadPairFindings();   // a stale generation shows the current one
    }
  };

  const toggleHistory = async (e: Engagement) => {
    if (history[e.id]) {
      setHistory((h) => {
        const n = { ...h };
        delete n[e.id];
        return n;
      });
      return;
    }
    setHistory((h) => ({ ...h, [e.id]: 'loading' }));
    try {
      const terms = await engagementsApi.history(e.id);
      setHistory((h) => ({ ...h, [e.id]: terms }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load the history.'));
      setHistory((h) => {
        const n = { ...h };
        delete n[e.id];
        return n;
      });
    }
  };

  return (
    <Card className="border-border" data-testid="engagements-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2 text-foreground">
            <Briefcase className="w-4 h-4" /> Employment &amp; Engagements
          </CardTitle>
          {rows && <p className="text-xs text-muted-foreground mt-1">{countLine(rows)}</p>}
        </div>
        {canManage && (
          <Button size="sm" className="gap-1" onClick={() => setWizardOpen(true)}>
            <Plus className="w-4 h-4" /> Add engagement
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {!isSelf && <RepaymentMoveCallout personId={employeeId} personName={personName} />}
        {error && <p className="text-sm text-danger-strong" role="alert">{error}</p>}
        {!error && rows === null && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading engagements…
          </p>
        )}
        {rows && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">No engagements to show.</p>
        )}
        {/* The block also stands with NO findings while a change is being synced,
            after it, or on an error — those notices must never disappear with the list. */}
        {canSeePairTax && (pairError || (pairFindings && pairFindings.length > 0) || pairSync !== 'idle') && (
          <div className="rounded-md border border-border p-3 space-y-2" data-testid="pair-tax-findings">
            <p className="text-sm font-medium text-foreground">{t('engagements.pairTax.sectionTitle')}</p>
            {pairError && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{pairError}</p>}
            {!pairError && pairSync === 'syncing' && (
              <p className="text-xs text-muted-foreground" data-testid="pair-tax-sync">{t('engagements.pairTax.syncing')}</p>
            )}
            {!pairError && pairSync === 'unchanged' && (
              <p className="text-xs text-muted-foreground" data-testid="pair-tax-sync">{t('engagements.pairTax.notYetUpdated')}</p>
            )}
            {!pairError && pairSync === 'cleared' && (
              <p className="text-xs text-muted-foreground" data-testid="pair-tax-sync">{t('engagements.pairTax.noLongerReported')}</p>
            )}
            {pairFindings?.map((f) => {
              const state = pairFindingState(f);
              const company = companyName(f.legal_entity_id);
              const from = (f.overlap_from || '').slice(0, 10);
              const to = (f.overlap_to || '').slice(0, 10);
              return (
                <div key={f.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant={state === 'confirmed' ? 'success' : 'danger'}>HIGH</Badge>
                  <span className="text-foreground">
                    {to ? t('engagements.pairTax.overlap', { company, from, to })
                      : t('engagements.pairTax.overlapOpen', { company, from })}
                  </span>
                  {state === 'confirmed' && (
                    <span className="text-xs text-muted-foreground">
                      {t('engagements.pairTax.confirmed', { date: (f.confirmed_at || '').slice(0, 10), note: f.note || '' })}
                    </span>
                  )}
                  {state === 'provisional' && (
                    <span className="text-xs text-muted-foreground">{t('engagements.pairTax.provisional')}</span>
                  )}
                  {state === 'open' && canEditPay && (
                    <Button size="sm" variant="outline" disabled={busy === f.id} onClick={() => void confirmPairTax(f)}>
                      {t('engagements.pairTax.button')}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {rows?.map((e) => {
          const actions = actionsFor(e, { manage: canManage, approve: canApprove });
          const h = history[e.id];
          return (
            <div key={e.id} className="rounded-md border border-border p-3 space-y-2" data-testid="engagement-row">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{e.legal_entity_name || 'Company'}</span>
                <Badge variant={e.relationship_type === 'employee' ? 'info' : 'warning'}>
                  {RELATIONSHIP_LABEL[e.relationship_type]}
                </Badge>
                <Badge variant={STATUS_VARIANT[e.status]}>{STATUS_LABEL[e.status]}</Badge>
                {e.is_primary && <Badge variant="outline">Primary</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">
                {termsSummary(e.terms, e.relationship_type) || 'No terms recorded'} · from {e.start_date}
                {e.end_date ? ` to ${e.end_date}` : ''}
              </p>
              {e.status_reason && e.status !== 'active' && (
                <p className="text-xs text-muted-foreground">Note: {e.status_reason}</p>
              )}
              <div className="flex flex-wrap gap-2">
                {actions.map((a) => (
                  <Button key={a} size="sm" variant={a === 'approve' || a === 'submit' ? 'default' : 'outline'}
                    disabled={busy === e.id} onClick={() => void run(e, a)}>
                    {ACTION_LABEL[a]}
                  </Button>
                ))}
                {isSecondaryEmployment(e) && canViewPay && (
                  <Button size="sm" variant="outline" onClick={() => void showPay(e)}>Pay at {e.legal_entity_name || 'company'}</Button>
                )}
                {isSecondaryEmployment(e) && canEditPay && (
                  <>
                    <Button size="sm" variant="outline" disabled={busy === e.id} onClick={() => void setSalary(e)}>Set salary</Button>
                    <Button size="sm" variant="outline" disabled={busy === e.id} onClick={() => void confirmTax(e)}>Confirm tax treatment</Button>
                  </>
                )}
                <Button size="sm" variant="ghost" className="gap-1" onClick={() => void toggleHistory(e)}>
                  History {h ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </Button>
              </div>
              {pay[e.id] && <p className="text-sm text-foreground" data-testid="engagement-pay">Salary: {pay[e.id]}</p>}
              {isSecondaryEmployment(e) && !isSelf && (
                <AttendanceRulesEditor personId={employeeId} engagementId={e.id} company={e.legal_entity_name || 'this company'} />
              )}
              {h === 'loading' && <p className="text-xs text-muted-foreground">Loading history…</p>}
              {Array.isArray(h) && (
                <ul className="text-xs text-muted-foreground space-y-1" aria-label="Terms history">
                  {h.map((t) => (
                    <li key={`${t.effective_from}-${t.reason}`}>
                      {t.effective_from} – {t.effective_to || 'now'}: {termsSummary(t, e.relationship_type) || '—'}
                      {t.reason ? ` (${t.reason})` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </CardContent>
      <EngagementWizard
        open={wizardOpen}
        employeeId={employeeId}
        personName={personName}
        onClose={() => setWizardOpen(false)}
        onSaved={() => { void load(); void refreshPairFindingsAfterChange(); }}
      />
    </Card>
  );
};

export default EngagementsCard;
